<template>
    <v-container fluid>
        <v-row>
            <v-col cols="12">
                <v-row align="center" justify="center">
                    <v-col cols="12" sm="9" md="5">
                        <v-form ref="addressForm" v-model="formValid">
                            <v-text-field
                                id="styled-input"
                                v-model="inputedText"
                                color="teal darken-4"
                                class="styled-input"
                                clearable
                                :rules="[validationRules.requiredAddress]"
                                counter
                                maxlength="40"
                                @keydown.enter.prevent="doSearchAddress"
                            >
                                <template v-slot:label>
                                    {{
                                        $t(
                                            'Введите адрес лицевого счёта (пример: байзакова 221 1)'
                                        )
                                    }}
                                </template>
                            </v-text-field>
                        </v-form>
                    </v-col>
                </v-row>

                
                <v-row align="center" justify="center">
                    <v-btn
                        rounded
                        color="teal darken-4"
                        dark
                        :disabled="!formValid || accountAddressesLoading"
                        @click="doSearchAddress"
                    >
                        {{ $t('Поиск') }}
                    </v-btn>
               </v-row>

                <v-row align="center" justify="center">
                    <div class="text-center">
                        <h3>{{ $t(message) }}</h3>
                    </div>
                </v-row>
            </v-col>
        </v-row>

        <v-row align="center" justify="center">
            <div v-if="accountAddressesLoading" class="text-center">
                <v-progress-circular
                    :size="100"
                    indeterminate
                    color="teal"
                />
            </div>
        </v-row>

        <template v-if="showResults && !selectedAddress">
            <v-row align="center" justify="center">
                <div class="text-center title">
                    {{ $t('Выберите адрес') }}
                </div>
            </v-row>

            <v-row align="center" justify="center">
                <v-col cols="12" sm="4" md="4">
                    <div class="text-center">
                        <v-data-table
                            class="elevation-12"
                            :headers="headers"
                            :items="accountAddresses01"
                            :loading="loading"
                            item-key="account_f_id"
                            hide-default-footer
                            hide-default-header
                            no-data-text
                        >
                            <template v-slot:body="{ items }">
                                <tbody>
                                    <tr
                                        v-for="item in items"
                                        :key="item.account_f_id"
                                        class="address-row"
                                        @click="selectAddress(item)"
                                    >
                                        <td>{{ item.account_address }}</td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-data-table>
                    </div>
                </v-col>

                <v-col cols="12" sm="4" md="4">
                    <div class="text-center">
                        <v-data-table
                            class="elevation-12"
                            :headers="headers"
                            :items="accountAddresses02"
                            :loading="loading"
                            item-key="account_f_id"
                            hide-default-footer
                            hide-default-header
                            no-data-text
                        >
                            <template v-slot:body="{ items }">
                                <tbody>
                                    <tr
                                        v-for="item in items"
                                        :key="item.account_f_id"
                                        class="address-row"
                                        @click="selectAddress(item)"
                                    >
                                        <td>{{ item.account_address }}</td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-data-table>
                    </div>
                </v-col>

                <v-col cols="12" sm="4" md="4">
                    <div class="text-center">
                        <v-data-table
                            class="elevation-12"
                            :headers="headers"
                            :items="accountAddresses03"
                            :loading="loading"
                            item-key="account_f_id"
                            hide-default-footer
                            hide-default-header
                            no-data-text
                        >
                            <template v-slot:body="{ items }">
                                <tbody>
                                    <tr
                                        v-for="item in items"
                                        :key="item.account_f_id"
                                        class="address-row"
                                        @click="selectAddress(item)"
                                    >
                                        <td>{{ item.account_address }}</td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-data-table>
                    </div>
                </v-col>
            </v-row>
        </template>

        <template v-if="selectedAddress">
            <v-row align="center" justify="center">
                <v-col cols="12" sm="9" md="5">
                    <div class="text-center subtitle-1 mb-3">
                        {{ selectedAddress.account_address }}
                    </div>

                    <v-form ref="paymentForm" v-model="paymentFormValid">
                        <v-text-field
                            v-model="lastPaymentAmount"
                            :label="$t('Введите сумму последней оплаты по счету без указания тиын')"
                            color="teal darken-4"
                            inputmode="decimal"
                            autocomplete="off"
                            clearable
                            :rules="[
                                validationRules.requiredPayment,
                                validationRules.paymentFormat
                            ]"
                            @keydown="allowDecimalInput"
                            @paste.prevent="onPastePayment"
                            @input="normalizePaymentAmount"
                        />
                    </v-form>
                </v-col>
            </v-row>

            <v-row align="center" justify="center">
                <v-btn
                    text
                    color="teal darken-4"
                    class="mr-3"
                    @click="cancelAddressSelection"
                >
                    {{ $t('Выбрать другой адрес') }}
                </v-btn>

                <v-btn
                    rounded
                    color="teal darken-4"
                    dark
                    :disabled="!paymentFormValid"
                    @click="continueSearch"
                >
                    {{ $t('Проверить') }}
                </v-btn>
            </v-row>
        </template>
    </v-container>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';

export default {
    data() {
        return {
            formValid: true,
            paymentFormValid: false,
            loading: false,
            inputedText: '',
            selectedAddress: null,
            lastPaymentAmount: '',
            headers: [
                {
                    text: 'Адрес',
                    value: 'account_address'
                }
            ],
            message: '',
            validationRules: {
                required: value => {
                    return !value
                        ? this.$t('Не указан адрес')
                        : true;
                },

                requiredPayment: value => {
                    return value !== null &&
                        value !== undefined &&
                        String(value).trim() !== ''
                        ? true
                        : this.$t('validation.requiredPayment');
                },

                paymentFormat: value => {
                    if (
                        value === null ||
                        value === undefined ||
                        String(value).trim() === ''
                    ) {
                        return true;
                    }

                    return /^\d+([.,]\d{1,2})?$/.test(String(value))
                        ? true
                        : this.$t(
                            'Введите сумму с двумя знаками после точки'
                        );
                }
            }
        };
    },

    computed: {
        ...mapState('print', [
            'accountAddresses',
            'accountAddressesLoading'
        ]),

        ...mapGetters('print', [
            'accountAddresses01',
            'accountAddresses02',
            'accountAddresses03'
        ]),

        showResults() {
            return (
                this.inputedText &&
                this.accountAddresses &&
                this.accountAddresses.length > 0
            );
        }
    },

    watch: {
        inputedText() {
            this.selectedAddress = null;
            this.lastPaymentAmount = '';
        },

        '$i18n.locale'() {
            this.$nextTick(() => {
                if (this.$refs.paymentForm) {
                    this.$refs.paymentForm.validate();
                }
            });
        }
    },

    mounted() {
        this.$nextTick(() => {
            this.init();
        });
    },

    methods: {
        ...mapActions('print', [
            'init',
            'searchAddresses',
            'selectAccount',
            'checkLastPayment'
        ]),

        allowDecimalInput(event) {
            const allowedControlKeys = [
                'Backspace',
                'Delete',
                'Tab',
                'ArrowLeft',
                'ArrowRight',
                'Home',
                'End'
            ];

            if (
                allowedControlKeys.includes(event.key) ||
                event.ctrlKey ||
                event.metaKey
            ) {
                return;
            }

            if (!/^[0-9.,]$/.test(event.key)) {
                event.preventDefault();
                return;
            }

            const currentValue = String(this.lastPaymentAmount || '');

            if (
                (event.key === '.' || event.key === ',') &&
                /[.,]/.test(currentValue)
            ) {
                event.preventDefault();
                return;
            }

            const selectionStart = event.target.selectionStart;
            const selectionEnd = event.target.selectionEnd;

            const nextValue =
                currentValue.slice(0, selectionStart) +
                event.key +
                currentValue.slice(selectionEnd);

            if (!/^\d*(?:[.,]\d{0,2})?$/.test(nextValue)) {
                event.preventDefault();
            }
        },

        onPastePayment(event) {
            const pastedText = event.clipboardData
                .getData('text')
                .trim()
                .replace(',', '.');


            if (!/^\d+(?:\.\d{1,2})?$/.test(pastedText)) {
                return;
            }

            this.lastPaymentAmount = pastedText;
        },

        normalizePaymentAmount(value) {
            if (value === null || value === undefined) {
                this.lastPaymentAmount = '';
                return;
            }

            let normalized = String(value)
                .replace(/,/g, '.')
                .replace(/[^\d.]/g, '');

            const firstDotIndex = normalized.indexOf('.');

            if (firstDotIndex !== -1) {
                const integerPart = normalized.slice(0, firstDotIndex);
                const decimalPart = normalized
                    .slice(firstDotIndex + 1)
                    .replace(/\./g, '')
                    .slice(0, 2);

                normalized = `${integerPart}.${decimalPart}`;
            }

            this.lastPaymentAmount = normalized;
        },

        async doSearchAddress() {
            if (!this.formValid) {
                this.$refs.addressForm.validate();
                return;
            }

            this.selectedAddress = null;
            this.lastPaymentAmount = '';
            this.message = '';

            const recaptchaToken = await this.recaptcha('/searchAddress');

            await this.searchAddresses({
                addressText: this.inputedText.trim(),
                recaptchaToken
            });
        },

        

        selectAddress(value) {
            this.selectedAddress = value;
            this.lastPaymentAmount = '';

            this.$nextTick(() => {
                if (this.$refs.paymentForm) {
                    this.$refs.paymentForm.resetValidation();
                }
            });
        },

        async continueSearch() {
            if (!this.selectedAddress) {
                return;
            }

            const valid = this.$refs.paymentForm.validate();

            if (!valid) {
                return;
            }

            const lastPaymentAmount = Number(
                String(this.lastPaymentAmount).replace(',', '.')
            );

            await this.selectAccount({
                ...this.selectedAddress,
                lastPaymentAmount: lastPaymentAmount
            });

            try {
                const recaptchaToken = await this.recaptcha('/checkLastPayment');

                const result = await this.checkLastPayment(recaptchaToken);

                if (!result.__success) {
                    this.message ='validation.invalidLastPayment';
                    return;
                }

                await this.$router.push('/print');
            } catch (e) {
                

                this.message = 'validation.paymentCheckError';
            }
        },

        cancelAddressSelection() {
            this.selectedAddress = null;
            this.lastPaymentAmount = '';
            this.message = '';

            this.$nextTick(() => {
                if (this.$refs.paymentForm) {
                    this.$refs.paymentForm.resetValidation();
                }
            });
        },

        async recaptcha(action) {
            return await this.$recaptcha(action);
        }
    }
};
</script>

<style scoped>
.address-row {
    cursor: pointer;
}

.address-row:hover {
    background-color: #f5f5f5;
}
</style>